<script setup lang="ts">
import { ref, nextTick } from "vue";
import { useNotification } from "src/composables/useNotification";
import type { ContextMenuProps } from "primevue/contextmenu";
import type { FetchTicketResponse } from "src/api/models";
import type { Column } from "src/types/trello-board-type";
import { useTicketService } from "src/services/ticket-service";
import { createTrelloContextMenu } from "./menu/factory";
import { useMenuState } from "./menu/state";
import PlannedDateOverlay from "./menu/overlays/PlannedDateOverlay.vue";
import { TicketCreateDialog } from "src/components/ticket-edit";
import AddArticleDialog from "../ticket/AddArticleDialog.vue";

const menu = ref();
const contextMenuItems = ref<ContextMenuProps["model"]>([]);
const selectAssigneeRef = ref();
const selectAssigneeVisible = ref(false);
const addArticleDialogVisible = ref(false);

const {
  datePickerVisible,
  datePickerPosition,
  showDatePicker,
  hideDatePicker,
  isQuickViewDialogVisible,
  showQuickView,
  hideQuickView,
} = useMenuState();

const props = defineProps<{
  ticket: FetchTicketResponse | null;
  column: Column<FetchTicketResponse> | null;
  cutTicket: FetchTicketResponse | null;
}>();

const emits = defineEmits<{
  "update:ticket": [ticket: FetchTicketResponse];
  "update:ticket-planned-for": [ticket: FetchTicketResponse];
  cut: [ticket: FetchTicketResponse];
  paste: [ticket: FetchTicketResponse, column: Column<FetchTicketResponse>];
  assign: [];
  "open-ticket": [id: string];
  "create-ticket": [id: string];
}>();

const ticketService = useTicketService();
const { showInfo, showError } = useNotification();

const isCreateTicketDialogVisible = ref(false);
const createTicketDate = ref<string>('');

const showCreateTicket = (date: string) => {
  createTicketDate.value = date;
  isCreateTicketDialogVisible.value = true;
};

const showAddArticleDialog = () => {
  addArticleDialogVisible.value = true;
};

function handleArticleAdded() {
  // Refresh the ticket after an article is added
  if (props.ticket?.id) {
    ticketService.fetchTicket(props.ticket.id).then(updatedTicket => {
      emits('update:ticket', updatedTicket);
    });
  }
}

async function showMenu(event: MouseEvent) {
  await nextTick();

  contextMenuItems.value = createTrelloContextMenu(
    props.ticket,
    props.column,
    event,
    {
      selectAssigneeRef: selectAssigneeRef.value,
      showAddArticleDialog,
      showDatePicker,
      showQuickView,
      showCreateTicket,
      onCopyPhoneSuccess: () => showInfo({ message: "Phone number copied" })
    },
    ticketService,
    {
      onUpdateTicket: (ticket) => emits('update:ticket', ticket),
      onUpdateTicketPlannedFor: (ticket) => emits('update:ticket-planned-for', ticket),
      onCut: (ticket) => emits('cut', ticket),
      onPaste: (ticket, column) => emits('paste', ticket, column)
    },
    props.cutTicket,
    showError
  );

  menu.value?.show(event);
}

function handleDateSelect(newDate: string) {
  if (!props.ticket?.id) return;

  const updatedTicket = {
    ...props.ticket,
    scheduled_date: newDate,
  };

  hideDatePicker();

  ticketService.updateTicketPlannedDate(props.ticket.id, newDate).then(() => {
    emits("update:ticket-planned-for", updatedTicket);
  }).catch(() => {
    showError({ message: "Failed to reschedule ticket" });
  });
}

async function openAssigneeDrawer() {
  selectAssigneeVisible.value = true;
  await nextTick();
  if (!selectAssigneeRef.value) await nextTick();
  const { onSelect } = selectAssigneeRef.value?.openDrawer() || {};
  if (!onSelect) return;
  onSelect(async (entity: any) => {
    if (!props.ticket?.id) return;
    await ticketService.updateTicketAssignee(props.ticket.id, entity.id);
    emits("update:ticket", { ...props.ticket, assignee: entity });
  });
}

function openDatePicker(event: MouseEvent) {
  if (event.clientX === 0 && event.clientY === 0 && event.target instanceof Element) {
    const rect = event.target.getBoundingClientRect();
    datePickerPosition.value = { x: rect.left, y: rect.bottom };
    datePickerVisible.value = true;
  } else {
    showDatePicker(event);
  }
}

function openPicturesDialog() {
  showQuickView();
}

defineExpose({
  showMenu,
  openAssigneeDrawer,
  openDatePicker,
  openPicturesDialog,
});
</script>

<template>
  <ContextMenu ref="menu" :model="contextMenuItems" />

  <SelectEntity
    v-if="selectAssigneeVisible"
    ref="selectAssigneeRef"
    entity="assignees"
    :model-value="ticket?.assignee?.id"
    :return-entity="true"
    headless
  />

  <Dialog
    v-model:visible="isQuickViewDialogVisible"
    :header="`#${ticket?.number} - ${ticket?.title}`"
  >
    <TicketPicture
      :ticket-id="ticket?.id"
      :files="ticket?.files ?? []"
    />
  </Dialog>

  <AddArticleDialog
    v-model:visible="addArticleDialogVisible"
    :ticket-id="ticket?.id"
    @article-added="handleArticleAdded"
  />

  <PlannedDateOverlay
    v-if="ticket"
    :ticket="ticket"
    :visible="datePickerVisible"
    :position="datePickerPosition"
    @close="hideDatePicker"
    @update:scheduled-date="handleDateSelect"
  />

  <TicketCreateDialog
    v-model="isCreateTicketDialogVisible"
    :initial-scheduled-date="createTicketDate"
    @create-ticket="emits('create-ticket', $event)"
  />
</template>

<style scoped>
.floating-calendar {
  background: white;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
}
</style>
